import { Divider, Paper, Stack, Typography } from "@mui/material";
import { useRevokeConnectedAppMutation } from "Api/Mutations/Sso/useRevokeConnectedAppMutation";
import { useConnectedAppsQuery } from "Api/Queries/Sso/useConnectedAppsQuery";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { ConnectedAppRow } from "Components/Sso/ConnectedAppRow";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import type { ConnectedAppDto } from "Infrastructure/Api/Api";
import { type ReactNode, useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { toast } from "sonner";

const RESOURCES = Resources.Sso.ConnectedApps;

/**
 * The apps the person signed in to with Mo.one. Disconnecting withdraws the consent and kills the app's
 * refresh tokens, so its next sign-in asks again.
 */
export const ConnectedAppsPage = () => {
  const { t } = useResource();
  const { data, isLoading, isError } = useConnectedAppsQuery();
  const [appToDisconnect, setAppToDisconnect] =
    useState<ConnectedAppDto | null>(null);
  const disconnectDialog = useConfirmDialog();

  const { mutate: disconnect } = useRevokeConnectedAppMutation({
    onSuccess: () => {
      toast.success(t(RESOURCES.DisconnectSuccess));
      setAppToDisconnect(null);
    },
    onError: () => toast.error(t(RESOURCES.DisconnectError)),
  });

  if (isLoading) {
    return (
      <Layout>
        <CenteredCircularProgress />
      </Layout>
    );
  }

  if (isError || !data) {
    return (
      <Layout>
        <ErrorText>{t(RESOURCES.LoadError)}</ErrorText>
      </Layout>
    );
  }

  const handleDisconnectRequest = (app: ConnectedAppDto) => {
    setAppToDisconnect(app);
    disconnectDialog.open();
  };

  return (
    <Layout>
      {data.length === 0 ? (
        <Typography color="text.secondary">{t(RESOURCES.Empty)}</Typography>
      ) : (
        <Paper>
          <Stack divider={<Divider flexItem />}>
            {data.map((app) => (
              <ConnectedAppRow
                key={app.clientId}
                app={app}
                onDisconnect={() => handleDisconnectRequest(app)}
              />
            ))}
          </Stack>
        </Paper>
      )}
      <ConfirmDialog
        isOpen={disconnectDialog.isOpen}
        handleResult={disconnectDialog.handleResult(() => {
          if (appToDisconnect) {
            disconnect(appToDisconnect.clientId);
          }
        })}
        modalTitle={t(RESOURCES.DisconnectConfirmTitle)}
        modalContent={
          <Typography>
            {t(RESOURCES.DisconnectConfirm, {
              app: appToDisconnect?.displayName ?? "",
            })}
          </Typography>
        }
        isDestructive
      />
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(Resources.Pages.Titles.ConnectedApps)}
      backNavigation={{ mode: "historyStack", fallbackUrl: appUrl("settings") }}
    >
      <StyledPageWrapper>{children}</StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default ConnectedAppsPage;
